/**
 * Stations Settings Page (Phase 9.5)
 *
 * Manage organization stations - tracked areas within zones for activity counting.
 * Features search, zone filter, and modal form for create/edit.
 * Stations are passed to KPI analysis (Gemini) for activity counting.
 */

import { useState, useEffect } from 'react';
import {
  MapPinned,
  Plus,
  Search,
  AlertCircle,
  Loader2,
  Pencil,
  Trash2,
  X,
  Locate,
  ChevronDown,
} from 'lucide-react';
import { useZones, useDeleteZone, useCreateZone, useUpdateZone } from '../../hooks/useZones';
import { useStationLookup } from '../../hooks/useStations';
import { useCurrentUser } from '../../hooks/useUsers';
import type { ZoneWithStation, CreateZone } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

export function ZonesPage() {
  useDocumentTitle('Stations');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;

  const [searchQuery, setSearchQuery] = useState('');
  const [stationFilter, setStationFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<ZoneWithStation | null>(null);

  const { data: zones, isLoading, error } = useZones({
    search: searchQuery || undefined,
    stationId: stationFilter || undefined,
    includeInactive: showInactive,
  });

  const { data: stations } = useStationLookup();
  const deleteMutation = useDeleteZone();

  const handleCreate = () => {
    setEditingZone(null);
    setIsFormOpen(true);
  };

  const handleEdit = (zone: ZoneWithStation) => {
    setEditingZone(zone);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this station?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingZone(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-50">
            <MapPinned size={24} className="text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Stations
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Define tracked areas within zones for activity counting
            </p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreate}
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Add Station
          </button>
        )}
      </div>

      {/* Filters Bar — only show when there is data or an active search/filter */}
      {((zones && zones.length > 0) || searchQuery || stationFilter) && (
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[280px]">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search stations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="
                w-full rounded-xl border border-gray-200 bg-white
                pl-11 pr-4 py-2.5 text-sm
                placeholder:text-gray-400
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                transition-all duration-200
              "
            />
          </div>

          {/* Zone filter */}
          <div className="relative">
            <Locate
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <select
              value={stationFilter}
              onChange={(e) => setStationFilter(e.target.value)}
              className="
                appearance-none rounded-xl border border-gray-200 bg-white
                py-2.5 pl-9 pr-10
                text-sm text-gray-700
                transition-colors duration-200
                hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              "
            >
              <option value="">All Zones</option>
              {stations?.map((station) => (
                <option key={station.id} value={station.id}>
                  {station.name}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>

          {/* Inactive filter */}
          {isAdmin && (
            <label className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 bg-white text-guild-green focus:ring-guild-green focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-gray-600">
                Show inactive
              </span>
            </label>
          )}
        </div>
      )}

      {/* Content */}
      {error ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Failed to load stations
            </p>
            <p className="text-sm text-red-600">
              Please try refreshing the page or contact support if the issue persists.
            </p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={32} className="text-guild-green animate-spin" />
          <p className="mt-4 text-sm text-gray-500">Loading stations...</p>
        </div>
      ) : zones && zones.length > 0 ? (
        <ZoneList
          zones={zones}
          isAdmin={isAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
        />
      ) : (
        <EmptyState
          searchQuery={searchQuery}
          isAdmin={isAdmin}
          onCreateClick={handleCreate}
        />
      )}

      {/* Form Dialog */}
      {isFormOpen && (
        <ZoneForm
          zone={editingZone}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

// ============================================
// STATION LIST COMPONENT
// ============================================

interface ZoneListProps {
  zones: ZoneWithStation[];
  isAdmin: boolean;
  onEdit: (zone: ZoneWithStation) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function ZoneList({ zones, isAdmin, onEdit, onDelete, isDeleting }: ZoneListProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Station
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Parent Zone
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            {isAdmin && (
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {zones.map((zone) => (
            <tr
              key={zone.id}
              className={`
                group transition-colors
                ${zone.isActive ? 'hover:bg-gray-50/50' : 'bg-gray-50/30'}
              `}
            >
              {/* Name */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div
                    className={`
                      flex h-9 w-9 items-center justify-center rounded-lg
                      ${zone.isActive ? 'bg-violet-50' : 'bg-gray-100'}
                    `}
                  >
                    <MapPinned
                      size={16}
                      className={zone.isActive ? 'text-violet-600' : 'text-gray-400'}
                    />
                  </div>
                  <span
                    className={`
                      text-sm font-semibold
                      ${zone.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {zone.name}
                  </span>
                </div>
              </td>

              {/* Description */}
              <td className="px-6 py-4">
                {zone.description ? (
                  <span
                    className={`
                      text-sm line-clamp-2
                      ${zone.isActive ? 'text-gray-600' : 'text-gray-400'}
                    `}
                  >
                    {zone.description}
                  </span>
                ) : (
                  <span className="text-gray-300 italic text-sm">No description</span>
                )}
              </td>

              {/* Parent Zone */}
              <td className="px-6 py-4 whitespace-nowrap">
                {zone.stationName ? (
                  <div className="flex items-center gap-1.5">
                    <Locate
                      size={14}
                      className={zone.isActive ? 'text-gray-400' : 'text-gray-300'}
                    />
                    <span
                      className={`
                        text-sm
                        ${zone.isActive ? 'text-gray-600' : 'text-gray-400'}
                      `}
                    >
                      {zone.stationName}
                    </span>
                    {zone.stationCode && (
                      <code
                        className={`
                          text-[10px] font-mono px-1.5 py-0.5 rounded
                          ${zone.isActive ? 'bg-gray-100 text-gray-500' : 'bg-gray-50 text-gray-400'}
                        `}
                      >
                        {zone.stationCode}
                      </code>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-300 italic text-sm">Not assigned</span>
                )}
              </td>

              {/* Status */}
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`
                    inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold
                    ${zone.isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-gray-100 text-gray-500'
                    }
                  `}
                >
                  <span
                    className={`
                      h-1.5 w-1.5 rounded-full
                      ${zone.isActive ? 'bg-emerald-500' : 'bg-gray-400'}
                    `}
                  />
                  {zone.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>

              {/* Actions */}
              {isAdmin && (
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(zone)}
                      className="
                        inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                        text-sm font-medium text-gray-600
                        hover:bg-gray-100 hover:text-gray-900
                        transition-colors
                      "
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                    {zone.isActive && (
                      <button
                        onClick={() => onDelete(zone.id)}
                        disabled={isDeleting}
                        className="
                          inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                          text-sm font-medium text-red-600
                          hover:bg-red-50 hover:text-red-700
                          disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors
                        "
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// STATION FORM COMPONENT
// ============================================

interface ZoneFormProps {
  zone: ZoneWithStation | null;
  onClose: () => void;
}

function ZoneForm({ zone, onClose }: ZoneFormProps) {
  const isEdit = !!zone;
  const [name, setName] = useState(zone?.name ?? '');
  const [stationId, setStationId] = useState(zone?.stationId ?? '');
  const [description, setDescription] = useState(zone?.description ?? '');
  const [error, setError] = useState<string | null>(null);

  const { data: stations } = useStationLookup();
  const createMutation = useCreateZone();
  const updateMutation = useUpdateZone();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when zone changes
  useEffect(() => {
    setName(zone?.name ?? '');
    setStationId(zone?.stationId ?? '');
    setDescription(zone?.description ?? '');
    setError(null);
  }, [zone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      if (isEdit && zone) {
        await updateMutation.mutateAsync({
          id: zone.id,
          data: {
            name: name.trim(),
            stationId: stationId || undefined,
            description: description.trim() || undefined,
          },
        });
      } else {
        const data: CreateZone = {
          name: name.trim(),
          stationId: stationId || undefined,
          description: description.trim() || undefined,
        };
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50">
              <MapPinned size={20} className="text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEdit ? 'Edit Station' : 'New Station'}
              </h2>
              <p className="text-xs text-gray-500">
                {isEdit ? 'Update station details' : 'Create a tracked area for activity counting'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Capper Area, Material Staging, QC Area"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
                autoFocus
              />
            </div>

            {/* Zone Field */}
            <div>
              <label
                htmlFor="stationId"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Zone
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <div className="relative">
                <select
                  id="stationId"
                  value={stationId}
                  onChange={(e) => setStationId(e.target.value)}
                  disabled={isPending}
                  className="
                    w-full appearance-none rounded-xl border border-gray-200 bg-white
                    px-4 py-3 pr-10 text-sm
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                >
                  <option value="">No zone</option>
                  {stations?.map((station) => (
                    <option key={station.id} value={station.id}>
                      {station.name} {station.code && `(${station.code})`}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
              <p className="mt-1.5 text-xs text-gray-400">
                Associate this station with a zone for targeted analytics
              </p>
            </div>

            {/* Description Field */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Description
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this zone is used for and any identification features..."
                rows={3}
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  resize-none
                  transition-all duration-200
                "
                disabled={isPending}
              />
              <p className="mt-1.5 text-xs text-gray-400">
                This description helps the AI identify the station in video footage
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="
                px-5 py-2.5 text-sm font-semibold text-gray-600
                rounded-xl hover:bg-gray-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="
                inline-flex items-center gap-2 px-5 py-2.5
                text-sm font-semibold text-white bg-guild-green
                rounded-xl shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              {isPending && (
                <Loader2 size={16} className="animate-spin" />
              )}
              {isEdit ? 'Save Changes' : 'Create Station'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// EMPTY STATE COMPONENT
// ============================================

interface EmptyStateProps {
  searchQuery: string;
  isAdmin: boolean;
  onCreateClick: () => void;
}

function EmptyState({ searchQuery, isAdmin, onCreateClick }: EmptyStateProps) {
  const isSearching = !!searchQuery;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 mb-5">
          <MapPinned size={32} className="text-gray-300" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {isSearching ? 'No stations found' : 'No stations yet'}
        </h3>

        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          {isSearching
            ? `No stations match "${searchQuery}". Try a different search term.`
            : 'Get started by creating stations to track operator activity in specific areas like capper areas, material staging, or quality control.'}
        </p>

        {isAdmin && !isSearching && (
          <button
            onClick={onCreateClick}
            className="
              mt-6 inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Create First Station
          </button>
        )}
      </div>
    </div>
  );
}

export default ZonesPage;
